import axios from "axios";

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000",
  withCredentials: true, // send/receive the http-only session cookie (CLAUDE.md auth model)
});

/** Request a login code. `identifier` is an email string ("email" channel) or E.164 mobile
 * ("whatsapp" channel, Task 9). */
export async function requestOTP(channel, identifier) {
  const body = channel === "email" ? { channel, email: identifier } : { channel, mobile: identifier };
  const { data } = await api.post("/auth/otp/request", body);
  return data;
}

/** Verify a login code and establish the session. Same identifier/channel shape as requestOTP. */
export async function verifyOTP(channel, identifier, code) {
  const body = channel === "email" ? { channel, email: identifier, code } : { channel, mobile: identifier, code };
  const { data } = await api.post("/auth/otp/verify", body);
  return data;
}

export async function fetchCurrentMember() {
  const { data } = await api.get("/auth/me");
  return data;
}

export async function logout() {
  await api.post("/auth/logout");
}

/** Returns null if the member hasn't created a profile yet (backend 404), instead of throwing —
 * "no profile" is an expected, common state, not an error. */
export async function fetchProfile() {
  try {
    const { data } = await api.get("/profile");
    return data;
  } catch (err) {
    if (err.response?.status === 404) return null;
    throw err;
  }
}

export async function saveProfile(payload) {
  const { data } = await api.put("/profile", payload);
  return data;
}

export async function deleteProfile() {
  await api.delete("/profile");
}

export async function deleteAccount() {
  await api.delete("/profile/account");
}

export async function createScenario(payload) {
  const { data } = await api.post("/scenarios", payload);
  return data;
}

export async function fetchScenarios() {
  const { data } = await api.get("/scenarios");
  return data;
}

export async function deleteScenario(id) {
  await api.delete(`/scenarios/${id}`);
}

/* -------------------------- Policies (Phase 2) -------------------------- */

export async function fetchPolicies() {
  const { data } = await api.get("/policies");
  return data;
}

export async function fetchPoliciesSummary() {
  const { data } = await api.get("/policies/summary");
  return data;
}

export async function createPolicy(payload) {
  const { data } = await api.post("/policies", payload);
  return data;
}

export async function updatePolicy(id, payload) {
  const { data } = await api.put(`/policies/${id}`, payload);
  return data;
}

export async function deletePolicy(id) {
  await api.delete(`/policies/${id}`);
}

export async function fetchPremiumCalendar(horizonMonths = 12) {
  const { data } = await api.get("/policies/calendar", { params: { horizon_months: horizonMonths } });
  return data;
}

// Policy PDF attachment (user-uploaded policy document)
export async function uploadPolicyPdf(id, file, onProgress) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/policies/${id}/pdf`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress
      ? (e) => onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0)
      : undefined,
    timeout: 180000,
  });
  return data;
}

export function policyPdfUrl(id) {
  const base = api.defaults.baseURL || "";
  return `${base}/policies/${id}/pdf`;
}

export async function removePolicyPdf(id) {
  const { data } = await api.delete(`/policies/${id}/pdf`);
  return data;
}

/* ---------------------- Health Policy Analyzer (Phase 3) ---------------------- */

export async function fetchAnalyzerCriteria() {
  const { data } = await api.get("/policies/analyses/criteria");
  return data;
}

export async function analyzePolicyPDF({ files, password, policyId, onProgress }) {
  const form = new FormData();
  const list = Array.isArray(files) ? files : [files].filter(Boolean);
  list.forEach((f) => form.append("files", f));
  if (password) form.append("password", password);
  if (policyId) form.append("policy_id", policyId);
  const { data } = await api.post("/policies/analyze", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress
      ? (e) => onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0)
      : undefined,
    timeout: 180000,
  });
  return data;
}

export async function fetchAnalyses() {
  const { data } = await api.get("/policies/analyses");
  return data;
}

export async function fetchAnalysis(id) {
  const { data } = await api.get(`/policies/analyses/${id}`);
  return data;
}

export async function deleteAnalysis(id) {
  await api.delete(`/policies/analyses/${id}`);
}

export async function reanalyzePolicy(id) {
  const { data } = await api.post(`/policies/analyses/${id}/reanalyze`);
  return data;
}

// ---------------------------------------------------------------------------
// Chat (Chatbase proxy)
// ---------------------------------------------------------------------------

/** List the current member's Chatbase conversations (metadata only). */
export async function fetchChatConversations() {
  const { data } = await api.get("/chat/conversations");
  return data;
}

/** Return full message history for one Chatbase conversation. */
export async function fetchChatMessages(conversationId) {
  const { data } = await api.get(`/chat/conversations/${conversationId}/messages`);
  return data;
}

/** POST /chat/send and stream the SSE response. Yields decoded text chunks as they arrive.
 * Consumer is responsible for parsing the SSE `type:text-delta` events (Chatbase format).
 * We use fetch() directly (not axios) because axios doesn't expose ReadableStream in the browser.
 * @param {{message: string, conversationId?: string, signal?: AbortSignal}} args
 * @returns {Promise<ReadableStreamDefaultReader<Uint8Array>>}
 */
export async function streamChat({ message, conversationId, signal }) {
  const resp = await fetch(`${api.defaults.baseURL}/chat/send`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, conversation_id: conversationId }),
    signal,
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => "");
    const err = new Error(detail || `Chat request failed (${resp.status})`);
    err.status = resp.status;
    throw err;
  }
  return resp.body.getReader();
}

export function analysisPdfUrl(id, docIndex = 0) {
  // Path only — caller composes it with the axios base or opens directly (cookie sent).
  const base = api.defaults.baseURL || "";
  const suffix = docIndex ? `?doc_index=${docIndex}` : "";
  return `${base}/policies/analyses/${id}/pdf${suffix}`;
}

function triggerPdfDownload(blobData, filenameBase) {
  const blob = new Blob([blobData], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filenameBase.toLowerCase().replace(/\s+/g, "-").replace(/\//g, "-")}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/** POST a calculator result to the backend and trigger a browser download of the branded PDF. */
export async function downloadCalculatorPDF(payload) {
  const response = await api.post("/pdf/export", payload, { responseType: "blob" });
  triggerPdfDownload(response.data, payload.calculator_title);
}

/** POST a multi-section report to the backend and download the branded multi-page PDF (Task 16.2). */
export async function downloadReportPDF(payload) {
  const response = await api.post("/pdf/report", payload, { responseType: "blob" });
  triggerPdfDownload(response.data, payload.report_title);
}

/* ------------------------- Net Worth (Assets & Liabilities) -------------------------- */

export async function fetchAssetTypes() {
  const { data } = await api.get("/asset-types");
  return data;
}

export async function fetchLiabilityTypes() {
  const { data } = await api.get("/liability-types");
  return data;
}

export async function fetchAssets() {
  const { data } = await api.get("/assets");
  return data;
}
export async function createAsset(payload) {
  const { data } = await api.post("/assets", payload);
  return data;
}
export async function updateAsset(id, payload) {
  const { data } = await api.put(`/assets/${id}`, payload);
  return data;
}
export async function deleteAsset(id) {
  await api.delete(`/assets/${id}`);
}
export async function bulkDeleteAssets(ids) {
  const { data } = await api.post("/assets/bulk-delete", { ids });
  return data;  // { deleted, requested }
}

export async function fetchLiabilities() {
  const { data } = await api.get("/liabilities");
  return data;
}
export async function createLiability(payload) {
  const { data } = await api.post("/liabilities", payload);
  return data;
}
export async function updateLiability(id, payload) {
  const { data } = await api.put(`/liabilities/${id}`, payload);
  return data;
}
export async function deleteLiability(id) {
  await api.delete(`/liabilities/${id}`);
}
export async function bulkDeleteLiabilities(ids) {
  const { data } = await api.post("/liabilities/bulk-delete", { ids });
  return data;  // { deleted, requested }
}

export async function fetchNetWorth() {
  const { data } = await api.get("/net-worth");
  return data;
}

/* ---------------------- Policies — Sum-Assured & Payment Calendar --------------------- */

export async function fetchSumAssuredTimeline() {
  const { data } = await api.get("/policies/sum-assured-timeline");
  return data;
}

export async function fetchFullPremiumSchedule(maxYears = 40, includePast = true) {
  const { data } = await api.get(
    `/policies/premium-schedule?max_years=${maxYears}&include_past=${includePast}`
  );
  return data;
}

/** Mark one premium due-date as paid on a policy. Idempotent. */
export async function markPremiumPaid(policyId, dueDate) {
  const { data } = await api.post(`/policies/${policyId}/paid`, { due_date: dueDate });
  return data;
}

/** Undo a mark-paid. Idempotent. */
export async function unmarkPremiumPaid(policyId, dueDate) {
  const { data } = await api.delete(`/policies/${policyId}/paid`, { params: { due_date: dueDate } });
  return data;
}

/* ---------------------------------- CAS import --------------------------------------- */

/** POST a CAS PDF + credentials. Returns a preview_id + parsed schemes for confirmation. */
export async function previewCAS({ file, pan, dob }) {
  const form = new FormData();
  form.append("pdf", file);
  form.append("pan", pan || "");
  form.append("dob", dob || "");
  const { data } = await api.post("/cas/preview", form, {
    headers: { "Content-Type": "multipart/form-data" },
    // CAS PDFs are small (<2MB typically) but the parse can take 3–6 sec.
    timeout: 60000,
  });
  return data;
}

/** POST user's confirmed selections. Backend creates / updates matching Assets. */
export async function importCAS(payload) {
  const { data } = await api.post("/cas/import", payload);
  return data;
}

/* ------------------------------- Budgets (member) ------------------------------ */

export async function fetchBudgetCategories() {
  const { data } = await api.get("/budgets/categories");
  return data;
}

export async function fetchBudgetPeriods() {
  const { data } = await api.get("/budgets/periods");
  return data;
}

export async function fetchBudget(periodKey) {
  const { data } = await api.get(`/budgets/${periodKey}`);
  return data;
}

export async function saveBudget(periodKey, payload) {
  const { data } = await api.put(`/budgets/${periodKey}`, payload);
  return data;
}

export async function deleteBudget(periodKey) {
  await api.delete(`/budgets/${periodKey}`);
}

export async function copyBudgetFrom(periodKey, sourcePeriodKey) {
  // Returns an unsaved draft — same shape as fetchBudget(), with `is_saved: false`.
  const { data } = await api.post(`/budgets/${periodKey}/copy-from`, {
    source_period_key: sourcePeriodKey,
  });
  return data;
}


/* ------------------------------- CMS Pages (public) ------------------------------ */

export async function fetchPublicPage(slug) {
  // Public read — no auth cookie required.
  const { data } = await api.get(`/pages/${slug}`);
  return data;
}


/* ------------------------------- Insurer KPIs (admin) ------------------------------ */

export async function fetchInsurerKpis() {
  const { data } = await api.get("/admin/insurer-kpis");
  return data;
}

export async function saveInsurerKpi(payload) {
  const { data } = await api.post("/admin/insurer-kpis", payload);
  return data;
}

export async function updateInsurerKpi(id, payload) {
  const { data } = await api.put(`/admin/insurer-kpis/${id}`, payload);
  return data;
}

export async function deleteInsurerKpi(id) {
  await api.delete(`/admin/insurer-kpis/${id}`);
}

export async function parseInsurerKpiUpload({ files, fiscalYear, onProgress }) {
  const form = new FormData();
  (Array.isArray(files) ? files : [files]).filter(Boolean).forEach((f) => form.append("files", f));
  if (fiscalYear) form.append("fiscal_year", fiscalYear);
  const { data } = await api.post("/admin/insurer-kpis/parse", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress
      ? (e) => onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0)
      : undefined,
    timeout: 240000,
  });
  return data;
}

export async function saveInsurerKpiBatch(payload) {
  const { data } = await api.post("/admin/insurer-kpis/save", payload);
  return data;
}


/* ------------------------------- Portfolio timeline ------------------------------ */

export async function fetchPortfolioTimeline() {
  const { data } = await api.get("/cas/timeline");
  return data;
}

export async function fetchCasSnapshots() {
  const { data } = await api.get("/cas/snapshots");
  return data;
}

export async function fetchCasSnapshot(id) {
  const { data } = await api.get(`/cas/snapshots/${id}`);
  return data;
}

export async function fetchCasSnapshotDiff(id) {
  const { data } = await api.get(`/cas/snapshots/${id}/diff`);
  return data;
}

export async function deleteCasSnapshot(id) {
  await api.delete(`/cas/snapshots/${id}`);
}

